<mat-sidenav-container
  class="chat-drawer-container"
  data-testid="chat-shell"
  [attr.data-open]="(state$ | async)?.isOpen || false"
  [attr.data-docked]="(state$ | async)?.isDocked || false"
  [attr.data-session-id]="(state$ | async)?.currentSession?.sessionId || ''"
  [attr.data-session-model]="(state$ | async)?.currentSession?.modelName || ''"
  [attr.data-context-project-id]="(state$ | async)?.context?.projectId || ''"
  [attr.data-context-file-id]="(state$ | async)?.context?.fileId || ''"
  [attr.data-context-page-number]="(state$ | async)?.context?.pageNumber || ''"
  [attr.data-selected-model]="selectedModel"
  [class.docked]="(state$ | async)?.isDocked"
  [hasBackdrop]="false"
>
  <mat-sidenav
    mode="side"
    position="end"
    [opened]="(state$ | async)?.isOpen || false"
    [style.width.px]="effectiveChatWidth"
    [fixedInViewport]="true"
    [fixedTopGap]="64"
    [fixedBottomGap]="0"
    class="chat-drawer"
    data-testid="chat-drawer"
    [class.docked]="(state$ | async)?.isDocked"
  >
    <div class="resize-handle" (mousedown)="startResize($event)"></div>
    <mat-card
      class="chat-panel"
      data-testid="chat-panel"
      [class.minimizing]="isMinimizing"
      [class.maximizing]="isMaximizing"
    >
      <!-- Header -->
      <mat-card-header class="chat-header">
        <div class="header-title-group">
          @if (state$ | async; as state) {
            <button
              mat-icon-button
              (click)="toggleDocked()"
              [matTooltip]="state.isDocked ? 'Undock panel (overlay)' : 'Dock panel (push content)'"
              data-testid="chat-dock-toggle"
              class="pin-button"
              [class.active]="state.isDocked"
            >
              <mat-icon>push_pin</mat-icon>
            </button>
          }
          <mat-card-title>PermitProof Assistant</mat-card-title>
        </div>
        <div class="header-actions">
          <button
            mat-icon-button
            (click)="confirmNewChat()"
            matTooltip="New Chat"
            data-testid="chat-new"
            class="new-chat-button"
          >
            <mat-icon>add</mat-icon>
          </button>

          <button
            mat-icon-button
            [matMenuTriggerFor]="historyMenu"
            #historyTrigger="matMenuTrigger"
            matTooltip="Past conversations"
            data-testid="chat-history-toggle"
            class="history-button"
            [class.history-highlight]="isHistoryHighlighted"
          >
            <mat-icon>history</mat-icon>
          </button>

          <mat-menu #historyMenu="matMenu" class="history-menu-panel" (menuOpened)="onHistoryMenuOpened()">
            <app-chat-history-list
              #historyList
              [currentSessionId]="(state$ | async)?.currentSession?.sessionId || null"
              (sessionSelected)="onSessionSelected($event)"
              (sessionDeleted)="onSessionDeleted($event)"
            ></app-chat-history-list>
          </mat-menu>

          <button
            mat-icon-button
            [matMenuTriggerFor]="overflowMenu"
            matTooltip="More options"
            class="overflow-menu-button"
            aria-label="More options"
            aria-haspopup="menu"
          >
            <mat-icon>more_vert</mat-icon>
          </button>

          <mat-menu #overflowMenu="matMenu">
            <button mat-menu-item (click)="toggleLlmDetails()">
              <mat-icon>{{ showLlmDetails ? 'visibility_off' : 'visibility' }}</mat-icon>
              <span>{{ showLlmDetails ? 'Hide' : 'Show' }} LLM details</span>
            </button>
            <button mat-menu-item (click)="copySessionId()">
              <mat-icon>tag</mat-icon>
              <span>Copy session ID</span>
            </button>
            <button mat-menu-item (click)="copyChatLog()">
              <mat-icon>content_copy</mat-icon>
              <span>Copy chat log</span>
            </button>
            <mat-divider></mat-divider>
            <button mat-menu-item (click)="openPrintPreview()">
              <mat-icon>preview</mat-icon>
              <span>Show print preview</span>
            </button>
            <button mat-menu-item (click)="downloadAsPdf()" [disabled]="isExportingPdf">
              @if (isExportingPdf) {
                <mat-spinner diameter="20"></mat-spinner>
              }
              @if (!isExportingPdf) {
                <mat-icon>picture_as_pdf</mat-icon>
              }
              <span>Download as PDF</span>
            </button>
            <mat-divider></mat-divider>
            <button mat-menu-item (click)="openFeedbackDialog()">
              <mat-icon>bug_report</mat-icon>
              <span>Report an issue</span>
            </button>
          </mat-menu>

          <button
            mat-icon-button
            (click)="close()"
            aria-label="Close chat"
            data-testid="chat-close"
            class="close-button"
          >
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </mat-card-header>

      <!-- Global indeterminate bar specifically to bridge Chromium cold starts during PDF export -->
      @if (isExportingPdf) {
        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      }

      <!-- Loading Progress Bar -->
      @if ((state$ | async)?.isLoading) {
        <mat-progress-bar mode="indeterminate" class="chat-progress-bar"></mat-progress-bar>
      }

      <!-- Messages -->
      <mat-card-content #messageContainer class="messages-container" (scroll)="onScroll()">
        @if (((state$ | async)?.messages?.length || 0) === 0) {
          <div class="empty-state" data-testid="chat-empty-state">
            <img src="assets/images/permitproof-logo.svg" alt="PermitProof" class="empty-state-logo" />
            <p>Hi! I'm your PermitProof assistant.</p>
            <p>Ask me about architectural plans, compliance codes, or anything else!</p>
            <div class="suggested-queries">
              <button mat-stroked-button (click)="sendSuggestion('What can you help me with?')">
                What can you help me with?
              </button>
              <button mat-stroked-button (click)="sendSuggestion('List my recent projects')">
                List my recent projects
              </button>
            </div>
          </div>
        }

        <!-- Main conversation (exclude queued messages) -->
        @for (message of getConversationMessages() | async; track message.messageId) {
          <div class="message-wrapper" data-testid="chat-message-wrapper">
            <app-chat-message
              [message]="message"
              [showAvatar]="true"
              [retryDisabled]="(state$ | async)?.isLoading ?? false"
              (retryRequested)="retryFailedMessage($event)"
              (reportErrorRequested)="openFeedbackDialog($event)"
            ></app-chat-message>
          </div>
        }

        <!-- Queued messages section (separate from conversation) -->
        @if (getQueuedMessages() | async; as queuedMsgs) {
          <div class="queued-messages-section">
            @if (queuedMsgs.length > 0) {
              <div class="queue-separator">
                <span class="queue-label">Queued ({{ queuedMsgs.length }})</span>
              </div>
            }
            @for (message of queuedMsgs; track message) {
              <div class="message-wrapper">
                <app-chat-message
                  [message]="message"
                  [showAvatar]="true"
                  (reportErrorRequested)="openFeedbackDialog($event)"
                ></app-chat-message>
              </div>
            }
          </div>
        }

        <!-- Typing indicator removed - now shown inside assistant's message bubble -->

        @if ((state$ | async)?.error) {
          <div class="error-message" data-testid="chat-error-state" role="alert">
            <mat-icon>error</mat-icon>
            <span>{{ (state$ | async)?.error }}</span>
            <button mat-button (click)="openGlobalErrorReport()">
              <mat-icon>bug_report</mat-icon>
              Report this problem
            </button>
          </div>
        }

        @if (userScrolledUp) {
          <button
            mat-mini-fab
            class="scroll-to-bottom-fab"
            (click)="forceScrollToBottom()"
            matTooltip="Scroll to bottom"
            aria-label="Scroll to bottom"
          >
            <mat-icon>keyboard_arrow_down</mat-icon>
          </button>
        }
      </mat-card-content>

      <!-- Bottom Section: Input with embedded context -->
      <mat-card-actions class="bottom-section">
        <app-chat-input
          [projectId]="(state$ | async)?.context?.projectId || null"
          [disabled]="false"
          [isLoading]="(state$ | async)?.isLoading || false"
          [context]="(state$ | async)?.context"
          [costAnalysis]="(effectiveCost$ | async) ?? undefined"
          [selectedModel]="selectedModel"
          [selectedThinkingLevel]="selectedThinkingLevel"
          [customBudget]="customBudget"
          [sessionId]="(state$ | async)?.currentSession?.sessionId || null"
          (messageSent)="sendMessage($event)"
          (stopRequested)="stopGeneration()"
          (modelChanged)="onModelChanged($event)"
          (thinkingLevelChanged)="onThinkingLevelChanged($event)"
          (customBudgetChanged)="onCustomBudgetChanged($event)"
          (contextRemoved)="removeContext($event)"
          (pinnedPageRemoved)="unpinPage($event)"
          (addContextClicked)="openContextDialog()"
          (voiceTranscript)="onVoiceTranscript($event)"
          (voiceToolCall)="onVoiceToolCall($event)"
          (voiceThinking)="onVoiceThinking($event)"
          (voiceStartRequested)="onVoiceStartRequested()"
          (voiceReconnected)="onVoiceReconnected()"
          (voiceTurnComplete)="onVoiceTurnComplete()"
          (voiceTranscriptDecorated)="onVoiceTranscriptDecorated($event)"
          (voiceTranscriptCorrected)="onVoiceTranscriptCorrected($event)"
          (voiceError)="onVoiceError($event)"
          [showStatusBar]="showLlmDetails"
        ></app-chat-input>
      </mat-card-actions>
    </mat-card>
  </mat-sidenav>
</mat-sidenav-container>
